// Firebase App Check with Cloudflare Turnstile as the attestation provider.
//
// Firebase has no built-in Turnstile provider, so this is a "custom provider": the
// page runs an invisible Turnstile challenge, sends the result to our Cloud Function
// mintAppCheckToken, and receives a Firebase App Check token that the SDK then attaches
// to every Firestore and Realtime Database request. Nothing here identifies the user;
// Turnstile only confirms a real browser on our site.
import "firebase/compat/app-check";

const TURNSTILE_SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
// Public site key of the Turnstile widget (not a secret). Empty disables App Check.
export const TURNSTILE_SITE_KEY = "0x4AAAAAAFZqU7e_IJAnZ_wh";

let scriptPromise = null;
function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = TURNSTILE_SCRIPT;
      s.async = true;
      s.onload = () => resolve(window.turnstile);
      s.onerror = () => reject(new Error("could not load Turnstile"));
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

let container = null;
function widgetContainer() {
  if (!container) {
    container = document.createElement("div");
    container.id = "turnstile-container";
    container.style.position = "fixed";
    container.style.bottom = "8px";
    container.style.right = "8px";
    container.style.zIndex = "100003";
    document.body.appendChild(container);
  }
  return container;
}

/** Solve an invisible Turnstile challenge and resolve with its token. */
async function solveChallenge(siteKey) {
  const turnstile = await loadTurnstile();
  return new Promise((resolve, reject) => {
    const el = widgetContainer();
    let id = null;
    const cleanup = () => {
      if (id !== null) try { turnstile.remove(id); } catch (e) { /* ignore */ }
      id = null;
      el.innerHTML = "";
    };
    const timer = setTimeout(() => { cleanup(); reject(new Error("Turnstile timed out")); }, 120000);
    try {
      id = turnstile.render(el, {
        sitekey: siteKey,
        size: "normal",
        appearance: "interaction-only", // invisible unless Cloudflare needs a click
        callback: (token) => { clearTimeout(timer); cleanup(); resolve(token); },
        "error-callback": (code) => { clearTimeout(timer); cleanup(); reject(new Error("Turnstile error " + (code || ""))); },
        "expired-callback": () => { clearTimeout(timer); cleanup(); reject(new Error("Turnstile expired")); }
      });
    } catch (err) {
      clearTimeout(timer);
      cleanup();
      reject(err);
    }
  });
}

/**
 * Call mintAppCheckToken over plain fetch using the callable wire format. The SDK's
 * httpsCallable cannot be used here: it first asks App Check for a token, which is the
 * very request we are in the middle of serving, and the two wait on each other.
 */
async function mintToken(firebase, turnstileToken) {
  const projectId = firebase.app().options.projectId;
  const url = "https://us-central1-" + projectId + ".cloudfunctions.net/mintAppCheckToken";
  const controller = typeof AbortController === "function" ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), 20000) : null;
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ data: { turnstileToken } }),
      signal: controller ? controller.signal : undefined
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || !body.result || !body.result.token) {
      const err = body.error || {};
      throw new Error((err.status || "HTTP " + res.status) + (err.message ? ": " + err.message : ""));
    }
    return body.result;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

// How long a Firebase request may wait for a first token. Firebase holds every
// service's first request until the provider answers, so a slow or blocked challenge
// must not hold the whole app: after this the request goes ahead without a token and
// the challenge carries on in the background for the next one.
const FIRST_TOKEN_WAIT_MS = 8000;
const RETRY_MIN_MS = 30 * 1000;
const RETRY_MAX_MS = 10 * 60 * 1000;
const REUSE_MARGIN_MS = 10 * 60 * 1000;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Build the provider the SDK calls for tokens. Firebase 8 asks once per service
 * (Firestore, Realtime Database, Functions) with no de-duplication, so only one
 * challenge may run at a time and every caller shares its result; a fresh token is
 * reused until it is close to expiry, and failures back off instead of looping.
 */
function makeProvider(firebase, siteKey) {
  let inflight = null;
  let cached = null;
  let retryDelay = RETRY_MIN_MS;
  let notBefore = 0;

  async function fetchToken() {
    const wait = notBefore - Date.now();
    if (wait > 0) await sleep(wait);
    try {
      const turnstileToken = await solveChallenge(siteKey);
      const data = await mintToken(firebase, turnstileToken);
      const ttl = data.ttlMillis || 60 * 60 * 1000;
      console.log("App Check token issued; valid for " + Math.round(ttl / 3600000) + " h");
      retryDelay = RETRY_MIN_MS;
      cached = { token: data.token, expireTimeMillis: Date.now() + ttl };
      return cached;
    } catch (err) {
      notBefore = Date.now() + retryDelay;
      console.warn("App Check: " + (err && err.message ? err.message : err) + "; next attempt in " + Math.round(retryDelay / 1000) + " s");
      retryDelay = Math.min(retryDelay * 2, RETRY_MAX_MS);
      throw err;
    }
  }

  return {
    getToken: () => {
      if (cached && cached.expireTimeMillis - Date.now() > REUSE_MARGIN_MS) return Promise.resolve(cached);
      if (!inflight) {
        inflight = fetchToken().finally(() => { inflight = null; });
        inflight.catch(() => {}); // callers handle it; keep it out of "unhandled rejection"
      }
      return Promise.race([
        inflight,
        sleep(FIRST_TOKEN_WAIT_MS).then(() => { throw new Error("App Check token not ready yet; continuing without one"); })
      ]);
    }
  };
}

/**
 * Activate App Check on the given firebase app. Safe to call once at start-up; with no
 * site key configured it does nothing (and the console says so).
 */
export function activateAppCheck(firebase, siteKey = TURNSTILE_SITE_KEY) {
  if (!siteKey || siteKey.startsWith("__")) {
    console.log("App Check not configured (no Turnstile site key); skipping");
    return false;
  }
  if (typeof navigator !== "undefined" && navigator.webdriver) {
    // Automated browsers (our own test rig) cannot pass Turnstile; with enforcement on
    // they are refused by Firebase anyway, so skipping here only keeps their logs clean.
    console.log("App Check skipped for an automated browser");
    return false;
  }
  if (typeof firebase.appCheck !== "function") {
    console.warn("App Check module unavailable in this SDK build");
    return false;
  }
  const provider = makeProvider(firebase, siteKey);
  firebase.appCheck().activate(provider, true);
  return true;
}
